import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import _ from 'lodash-es';
import useHeaderContext from './common/hooks/useHeaderContext';
import useUserContext from './common/hooks/useUserContext';
import useSettingsContext from './common/hooks/useSettingsContext';
import useBreadcrumbContext from './common/hooks/useBreadcrumbContext';
import { getIdbItem } from './utils/clientStorage';
import trackNavigationHistory from './modules/core/services/navigationService';

function RouteChangeListener() {
  const location = useLocation();
  const headerData = useHeaderContext();
  const { setHeaderContext } = headerData;
  const { user } = useUserContext();
  const history = useHistory();
  const { dispatch } = useBreadcrumbContext();
  const { settingsData } = useSettingsContext();
  let sitemapBreadcrumbData;

  const getBreadCrumbsForUrl = (routeUrl) => {
    let path = routeUrl;
    if (_.endsWith(path, '/')) path = path.slice(0, path.length - 1);
    const data = sitemapBreadcrumbData.find((item) => item.routeUrl === path);
    if (!data) {
      const partialPath = path.slice(0, path.lastIndexOf('/'));
      if (!_.isEmpty(partialPath) && partialPath !== '/') {
        return getBreadCrumbsForUrl(partialPath);
      }
    }
    return data;
  };

  const updateBreadcrumbData = async (pathname) => {
    let routeUrl = pathname.replace('#', '');
    if (_.startsWith(routeUrl, '/c/')) {
      dispatch({
        type: 'CLEAR_BREADCRUMBS'
      });
    } else {
      sitemapBreadcrumbData = await getIdbItem('RCoreBreadCrumbData');
      if (sitemapBreadcrumbData != null && sitemapBreadcrumbData.length > 0) {
        if (routeUrl.indexOf('?') > -1) routeUrl = routeUrl.substring(0, routeUrl.indexOf('?'));
        const breadcrumbObj = getBreadCrumbsForUrl(routeUrl);
        if (breadcrumbObj)
          dispatch({
            type: 'POPULATE_BREADCRUMBS',
            data: {
              ...breadcrumbObj,
              isRcoreNode: true
            }
          });
      }
    }
  };
  // Track navigation history for rcore routes that are part of sitemap.
  const updateNavigationHistory = async (pathname) => {
    let routeUrl = pathname.replace('#', '');
    if (_.startsWith(routeUrl, '/c/')) return null;
    const captureNavigationHistory =
      settingsData && settingsData.settings
        ? settingsData.settings.filter((setting) => setting.key === 'CAPTURE_NAVIGATION_HISTORY')[0]
            ?.value
        : 'False';
    if (
      captureNavigationHistory !== null &&
      captureNavigationHistory.toLowerCase() === 'true' &&
      sitemapBreadcrumbData != null &&
      sitemapBreadcrumbData.length > 0
    ) {
      if (routeUrl.indexOf('?') > -1) routeUrl = routeUrl.substring(0, routeUrl.indexOf('?'));
      if (_.endsWith(routeUrl, '/')) routeUrl = routeUrl.slice(0, routeUrl.length - 1);
      const breadcrumbObj = sitemapBreadcrumbData.find((item) => item.routeUrl === routeUrl);
      if (breadcrumbObj) {
        const breadcrumbList = breadcrumbObj.breadcrumbData;
        const destinationValue = breadcrumbList[breadcrumbList.length - 1].nodeKey;
        const destinationUrl = breadcrumbList[breadcrumbList.length - 1].url;
        trackNavigationHistory('SiteMap', destinationValue, destinationUrl);
      }
    }
  };

  useEffect(() => {
    const pathname = location.pathname.toLowerCase();
    // Populate all the public routes which should skip permission check
    const publicRoutes = new Set(['/accessdenied']);

    const evaluatePermissions = (route) => {
      let path = route;

      if (user.accessibleRcoreRoutes.has(path)) {
        return true;
      }

      // check for accessing new dashboards
      if (user.accessibleRcoreRoutes.size !== 0 && path.startsWith('/dashboard/')) return false;

      const routeIndex = _.findIndex(user.securedRoutes, (r) => {
        return r === path;
      });

      if (routeIndex > -1) {
        return false;
      }
      if (_.endsWith(path, '/')) {
        path = path.slice(0, path.length - 1);
      }

      const partialPath = path.slice(0, path.lastIndexOf('/'));

      if (!_.isEmpty(partialPath) && partialPath !== '/') {
        return evaluatePermissions(partialPath);
      }
      return true;
    };

    /* Skip the checks in case of -
     * Public route - accessdenied
     * Base route - '/'
     * Invalid routes - 'rcore/usersecurity'
     * Core routes - '/c/'
     * Auth routes - '/authentication/'
     * User not having any accessible routes as the system would redirect the user to home page
     */

    const ignoreRouteCheck =
      publicRoutes.has(pathname) ||
      pathname === '/' ||
      _.startsWith(pathname, '/c/') ||
      _.startsWith(pathname, '/authentication') ||
      !user ||
      !user.accessibleRcoreRoutes;

    const allowAccess = ignoreRouteCheck ? true : evaluatePermissions(pathname);

    // Updating the headercontext resetSearchValues to true when there is change in location path
    setHeaderContext((state) => ({
      ...state,
      resetSearchValues: true
    }));

    if (!allowAccess) {
      history.push('/accessDenied');
    }
  }, [location.pathname, setHeaderContext, user, history]);

  useEffect(() => {
    (async () => {
      const pathname = location.pathname.toLowerCase();
      await updateBreadcrumbData(pathname);
      updateNavigationHistory(pathname);
      setTimeout(() => {
        const contentElement = document.getElementsByClassName('MuiBreadcrumbs-li')[0];
        if (contentElement !== undefined) {
          contentElement.firstChild.setAttribute('tabindex', '0');
          contentElement.firstChild.focus();
        }
      }, 500);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}
export default RouteChangeListener;
